import { Alert, AlertDescription, AlertTitle } from '@invent/wl-ui-kit-next/ui';
import { Users } from 'lucide-react';
import * as React from 'react';

import { formatDateTime } from '@/domains/survey';
import { presenceApi, type ContentEditKind, type IContentPresence } from './api/presence-api';

/** How often an open editor renews its mark. survey-api counts a mark for 60 seconds, so a single
 *  late request does not make anybody vanish. */
const HEARTBEAT_MS = 20_000;

/**
 * Marks `recordId` open while the calling editor is mounted, and answers who else has it open
 * (WEB-11 AC2). Null `recordId` marks nothing — the team page, with no panel open.
 *
 * A failed mark keeps the last answer rather than clearing it: the notice is comfort, and a
 * network blip should not tell somebody they are now alone with the record.
 */
export function useEditPresence(
  kind: ContentEditKind,
  recordId: Nullable<string>
): IContentPresence[] {
  const [others, setOthers] = React.useState<IContentPresence[]>([]);

  React.useEffect(() => {
    setOthers([]);
    if (!recordId) return undefined;
    const controller = new AbortController();
    const mark = () =>
      presenceApi
        .mark(kind, recordId, controller.signal)
        .then(setOthers)
        .catch(() => undefined);
    void mark();
    const timer = window.setInterval(() => void mark(), HEARTBEAT_MS);
    return () => {
      window.clearInterval(timer);
      controller.abort();
      void presenceApi.leave(kind, recordId).catch(() => undefined);
    };
  }, [kind, recordId]);

  return others;
}

const KIND_WORD: Record<ContentEditKind, string> = {
  post: 'post',
  member: 'person',
  event: 'event'
};

/** "Also open by …" over an editor. Nothing when nobody else has it open. */
export function EditPresenceNotice({
  kind,
  others
}: {
  kind: ContentEditKind;
  others: IContentPresence[];
}) {
  if (others.length === 0) return null;
  return (
    <Alert data-edit-presence>
      <Users className="h-4 w-4" aria-hidden />
      <AlertTitle>
        Also being edited by {others.map((other) => other.email).join(', ')}
      </AlertTitle>
      <AlertDescription>
        {others.length === 1 ? 'They have' : 'They have each'} had this {KIND_WORD[kind]} open since{' '}
        {others.map((other) => formatDateTime(other.openedAt)).join(', ')}. You can both keep
        working: whoever saves second is told, and nothing is overwritten.
      </AlertDescription>
    </Alert>
  );
}
